import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Promo Codes tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function PromoTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="promo-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4 flex items-center gap-2">
                    <Gift className="w-6 h-6 text-pink-500" /> Discount / Promo Codes
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Create promo codes for members to get discounts on memberships and services.
                  </p>

                  {/* Create New Promo Code Form */}
                  <div className="wm-card p-6 rounded-lg border border-border mb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Create New Promo Code</h3>
                    <form onSubmit={createPromoCode} className="grid md:grid-cols-6 gap-4" data-testid="promo-create-form">
                      <div>
                        <Label className="text-foreground">Code</Label>
                        <Input
                          placeholder="e.g., WELCOME20"
                          value={newPromo.code}
                          onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                          className="bg-input text-foreground mt-1"
                          required
                          data-testid="promo-code-input"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Type</Label>
                        <select
                          value={newPromo.discount_type}
                          onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                          className="bg-input text-foreground mt-1 h-10 w-full rounded-md px-2 border border-border"
                          data-testid="promo-type-select"
                        >
                          <option value="percent">Percent (%)</option>
                          <option value="fixed">Fixed amount ($)</option>
                        </select>
                      </div>
                      {newPromo.discount_type === 'percent' ? (
                        <div>
                          <Label className="text-foreground">Discount %</Label>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={newPromo.discount_percent}
                            onChange={(e) => setNewPromo({ ...newPromo, discount_percent: parseInt(e.target.value) || 0 })}
                            className="bg-input text-foreground mt-1"
                            data-testid="promo-percent-input"
                          />
                        </div>
                      ) : (
                        <div>
                          <Label className="text-foreground">Amount Off ($)</Label>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={newPromo.discount_amount}
                            onChange={(e) => setNewPromo({ ...newPromo, discount_amount: parseFloat(e.target.value) || 0 })}
                            className="bg-input text-foreground mt-1"
                            data-testid="promo-amount-input"
                          />
                        </div>
                      )}
                      <div>
                        <Label className="text-foreground">Max Uses</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newPromo.max_uses}
                          onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) || 100 })}
                          className="bg-input text-foreground mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Expires</Label>
                        <Input
                          type="date"
                          value={newPromo.expires_at}
                          onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
                          className="bg-input text-foreground mt-1"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white w-full" data-testid="promo-submit-btn">
                          Create Code
                        </Button>
                      </div>
                    </form>
                  </div>

                  {/* Existing Promo Codes */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Active Promo Codes</h3>
                    {promoCodes.length > 0 ? (
                      promoCodes.map((promo) => (
                        <div key={promo.id} className="wm-card p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="text-xl font-mono font-bold text-pink-400 bg-pink-500/10 px-3 py-1 rounded">
                                {promo.code}
                              </span>
                              <span className="text-lg font-semibold text-foreground">
                                {promo.discount_type === 'fixed'
                                  ? `$${(promo.discount_amount || 0).toFixed(2)} OFF`
                                  : `${promo.discount_percent || 0}% OFF`}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Used: {promo.times_used || 0} / {promo.max_uses} • 
                              {promo.expires_at ? ` Expires: ${new Date(promo.expires_at).toLocaleDateString()}` : ' No expiry'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setPromoToDelete(promo)}
                            data-testid={`promo-delete-btn-${promo.code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="wm-card p-8 rounded-lg text-center">
                        <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No promo codes created yet</p>
                      </div>
                    )}
                  </div>

                  <AlertDialog open={!!promoToDelete} onOpenChange={(open) => !open && setPromoToDelete(null)}>
                    <AlertDialogContent data-testid="promo-delete-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete promo code?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {promoToDelete && (
                            <>
                              This will permanently remove <span className="font-mono font-bold text-pink-400">{promoToDelete.code}</span>.
                              It can no longer be redeemed at checkout. This action cannot be undone.
                            </>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="promo-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => promoToDelete && deletePromoCode(promoToDelete.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="promo-delete-confirm"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
    </div>
  );
}
